import React, { createRef } from 'react';
import { connect } from 'react-redux';

import { RootState } from 'MyTypes';
import { omit } from 'lodash/fp';
import { compose } from 'redux';

import {
    FlexColumn,
    Loading,
    PageBody,
    PageBodyFooter,
    PageContainer,
    ScrollToTop,
} from '@portal/common/components';
import { TYPE_DATA_TABLE_CHART } from '@portal/common/models/chart-type';
import {
    CHART_SECTION,
    DATA_TABLE_SECTION,
    ONETIME_DATA_SECTION,
} from '@portal/common/models/data-explorer-section';
import { isDataToolAccessible } from '@portal/common/models/data-project';
import { CSV, PDF, XLS } from '@portal/common/models/file-format';
import { styled } from '@portal/common/theme';
import { DataExportFormat } from '@portal/common/types';
import { WithRouterProps, withRouter } from '@portal/common/utility/hoc/with-router';

import ChartSection from '../../components/DataExplorer/ChartSection';
import DataTableSection from '../../components/DataExplorer/DataTableSection';
import ToolbarSection from '../../components/DataExplorer/ToolbarSection';
import DataToolNotShared from '../../components/DataToolNotShared';
import _ from '../../locale';
import {
    dataSelectionValidation,
    downloadDataExportAsync,
    downloadPdfExportAsync,
    initializeDataTableDisplayState,
    initializeDataTypeSelection,
    initializeSelectedChartType,
    initializeSelectedDataType,
    initializeVisibleRefinementFilters,
    queryDataRendered,
    resetDataTool,
    validateSelectedRefinementFiltersComplexityLimit,
} from '../../store/data-explorer/actions';
import {
    getDataCollections,
    getDataToolConfig,
    getSelectedChartType,
    getSelectedConditions,
    getSelectedRefinementFiltersWithFallbackToDefaultFilters,
} from '../../store/data-explorer/selectors';
import { initializeDataCollection } from '../../store/user-settings/actions';
import { getSelectedDataCollectionForDataTool } from '../../store/user-settings/selectors';
import withLocationSyncCheck from '../../utility/hoc/with-location-sync-check';
import EDPHeader from './EDPHeader';
import EDPOneTimeDataSection from './EDPOnetimeDataSection';
import EDPSelectDataSection from './EDPSelectDataSection';

const ContentContainer = styled(FlexColumn)(({ theme }) => ({
    flexGrow: 1,
    marginTop: -30,
    '> :not(:last-child)': {
        marginBottom: 15,
        [theme.breakpoint.md]: {
            marginBottom: 30,
        },
    },
}));

const BackToTopAnchor = styled('div')({
    position: 'relative',
    left: -300,
    top: -100,
    width: 100,
});

const mapStateToProps = (state: RootState) => ({
    dataCollections: getDataCollections(state),
    selectedDataCollection: getSelectedDataCollectionForDataTool(state),
    selectedConditions: getSelectedConditions(state),
    selectedRefinementFilters: getSelectedRefinementFiltersWithFallbackToDefaultFilters(state),
    selectedChartType: getSelectedChartType(state),
    dataToolConfig: getDataToolConfig(state),
});

const dispatchProps = {
    queryDataRendered,
    initializeSelectedChartType,
    initializeSelectedDataType,
    initializeVisibleRefinementFilters,
    initializeDataCollection,
    resetDataTool,
    initializeDataTypeSelection,
    downloadDataExportRequest: downloadDataExportAsync.request,
    downloadPdfExportRequest: downloadPdfExportAsync.request,
    initializeDataTableDisplayState,
    queryDataValidationRequest: dataSelectionValidation.request,
    validateSelectedRefinementFiltersComplexityLimit:
        validateSelectedRefinementFiltersComplexityLimit,
};

type Props = ReturnType<typeof mapStateToProps> & typeof dispatchProps & WithRouterProps & {};

type State = {
    isVisiblePdfReportModal: boolean;
    isVisibleSharePermalinkModal: boolean;
    isVisibleCopiedPermalinkModal: boolean;
    areChartsVisible: boolean;
};

class EDPExplorerScene extends React.Component<Props, State> {
    state: State = {
        isVisibleSharePermalinkModal: false,
        isVisiblePdfReportModal: false,
        isVisibleCopiedPermalinkModal: false,
        areChartsVisible: true,
    };

    containerRef = createRef<HTMLDivElement>();

    componentDidMount() {
        if (this.areDataCollectionsLoaded()) {
            this.initialize();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (!prevProps.dataCollections && this.areDataCollectionsLoaded()) {
            this.initialize();
        }
    }

    getDataToolName = (): string => this.props.location.pathname;

    areDataCollectionsLoaded = (): boolean => {
        const { dataCollections } = this.props;
        return !!(dataCollections && dataCollections.length);
    };

    initialize = () => {
        const {
            dataCollections,
            location,
            history,
            dataToolConfig,
            selectedDataCollection,
            initializeSelectedChartType,
            initializeSelectedDataType,
            initializeVisibleRefinementFilters,
            initializeDataCollection,
            initializeDataTypeSelection,
            initializeDataTableDisplayState,
            queryDataValidationRequest,
            validateSelectedRefinementFiltersComplexityLimit,
        } = this.props;

        const initData: {
            dataCollectionId;
            conditions;
            refinementFilters;
            dataType;
            chartType;
            activeRefinements;
            configuration;
        } = {};

        const routerState = location.state;

        // initialize from DataExplorerSelectionResource
        if (routerState && routerState.dataLoaded) {
            const {
                dataCollectionId,
                conditions,
                refinementFilters,
                dataType,
                chartType,
                activeRefinements,
                configuration,
            } = location.state;

            initData.dataCollectionId = dataCollectionId;
            initData.conditions = conditions;
            initData.refinementFilters = refinementFilters;
            initData.dataType = dataType;
            initData.chartType = chartType;
            initData.activeRefinements = activeRefinements;
            initData.configuration = configuration;

            history.replace(
                '.',
                omit(
                    [
                        'dataCollectionId',
                        'conditions',
                        'refinementFilters',
                        'dataType',
                        'chartType',
                        'activeRefinements',
                        'configuration',
                    ],
                    routerState
                )
            );
        }
        let {
            dataCollectionId,
            conditions,
            refinementFilters,
            dataType,
            chartType,
            activeRefinements,
            configuration,
        } = initData;

        const dataProjectsDataCollections = (dataCollections || []).filter((collection) =>
            dataToolConfig.dataProjectIds.includes(collection.data_project_id)
        );

        if (dataCollectionId == null) {
            if (selectedDataCollection != null) {
                dataCollectionId = selectedDataCollection.id;
            } else if (dataProjectsDataCollections.length) {
                // fallback
                dataCollectionId = dataProjectsDataCollections[0].id;
            } else {
                return;
            }
        }

        // mandatory initializations
        initializeDataCollection({
            dataCollectionId,
            dataTool: this.getDataToolName(),
        });

        initializeSelectedDataType(dataType || dataToolConfig.defaultDataType);
        initializeSelectedChartType(chartType || dataToolConfig.defaultChartType);

        initializeDataTypeSelection({
            conditions: conditions || [],
            refinementFilters: refinementFilters || null,
        });

        validateSelectedRefinementFiltersComplexityLimit(refinementFilters);

        if (typeof configuration?.chartSettings?.areVisible !== 'undefined') {
            this.setState({ areChartsVisible: configuration?.chartSettings?.areVisible });
        }

        // optional initializations
        if (activeRefinements) {
            initializeVisibleRefinementFilters(activeRefinements);
        }

        initializeDataTableDisplayState(configuration);

        queryDataValidationRequest({
            conditions: conditions || [],
            refinementFilters: refinementFilters || null,
        });
    };

    handleExportData = (format: DataExportFormat) => {
        const { dataToolConfig, downloadDataExportRequest, selectedChartType } = this.props;
        const { sections } = dataToolConfig;

        let chart_types;
        switch (format) {
            case CSV:
            case XLS: {
                chart_types = [TYPE_DATA_TABLE_CHART];
                break;
            }
            case PDF: {
                chart_types = [selectedChartType];
                if (sections.includes(DATA_TABLE_SECTION)) {
                    chart_types.push(TYPE_DATA_TABLE_CHART);
                }
                break;
            }
            default: {
                chart_types = [selectedChartType];
                break;
            }
        }

        downloadDataExportRequest({ chart_types, format });
    };

    toggleChartsVisibility = () => {
        this.setState((state) => ({
            ...state,
            areChartsVisible: !state.areChartsVisible,
        }));
    };

    renderSections = () => {
        const { dataToolConfig, queryDataRendered } = this.props;
        const { areChartsVisible } = this.state;

        if (!dataToolConfig) {
            return null;
        }

        const { sections } = dataToolConfig;

        return (
            <>
                <EDPSelectDataSection
                    areChartsVisible={areChartsVisible}
                    toggleChartsVisibility={this.toggleChartsVisibility}
                />
                {sections.map((section) => {
                    switch (section) {
                        case DATA_TABLE_SECTION:
                            return (
                                <DataTableSection
                                    key={section}
                                    onExportData={this.handleExportData}
                                />
                            );
                        case CHART_SECTION:
                            return (
                                <ChartSection
                                    key={section}
                                    areChartsVisible={areChartsVisible}
                                    onExportData={this.handleExportData}
                                    onDataRendered={() => void queryDataRendered(Date.now())}
                                />
                            );
                        case ONETIME_DATA_SECTION:
                            return <EDPOneTimeDataSection key={section} />;
                        default: {
                            return null;
                        }
                    }
                })}
            </>
        );
    };

    render() {
        const { dataToolConfig, dataCollections } = this.props;
        const { areChartsVisible } = this.state;

        if (!dataToolConfig || !dataCollections) {
            return <Loading />;
        }

        if (!isDataToolAccessible(dataToolConfig.dataProjectIds, dataCollections)) {
            return <DataToolNotShared toolPath={this.getDataToolName()} isLarge />;
        }

        return (
            <PageContainer>
                <EDPHeader areChartsHidden={!areChartsVisible} />
                <PageBody transparentSurface innerRef={this.containerRef}>
                    <ContentContainer>
                        <BackToTopAnchor id="back-to-top-anchor" />
                        {this.renderSections()}
                        <ToolbarSection>
                            {_('custom_chart_scene_citation_text', {
                                date: new Date().toLocaleDateString('en-US'),
                            })}
                        </ToolbarSection>
                    </ContentContainer>
                    <PageBodyFooter>{_.get('footer_note')}</PageBodyFooter>
                </PageBody>
                <ScrollToTop target={this.containerRef?.current} />
            </PageContainer>
        );
    }
}

export default compose(
    withLocationSyncCheck,
    withRouter,
    connect(mapStateToProps, dispatchProps)
)(EDPExplorerScene);
